import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { CardHeading, getAgeRangeColor } from "./MediaKitComponents";

export type DemographyGenderRow = {
  type: string;
  confidence?: number;
  percentage?: number;
};

export type DemographyAgeRow = {
  range: string;
  confidence?: number;
  percentage?: number;
};

function pct(row: { confidence?: number; percentage?: number }): number {
  return row.confidence ?? row.percentage ?? 0;
}

/** Maps API gender tokens to fixed Spanish labels: Masculino / Femenino / Otro */
export function demographyGenderLabel(type: string): string {
  const raw = type.trim();
  const t = raw.toLowerCase().replace(/_/g, "");
  if (t === "male" || t === "masculino" || t === "m")
    return i18n.t("mediaKit:gender.male");
  if (t === "female" || t === "femenino" || t === "f")
    return i18n.t("mediaKit:gender.female");
  // YouTube OAuth enums e.g. GENDER_USER_SPECIFIED, camelCase genderUserSpecified
  if (
    t === "genderuserspecified" ||
    t === "userspecified" ||
    (t.includes("user") && t.includes("specified")) ||
    /user_?specified/i.test(raw)
  ) {
    return i18n.t("mediaKit:gender.unspecified");
  }
  if (t === "unknown" || t === "other" || t === "otro" || t === "undefined")
    return i18n.t("mediaKit:gender.other");
  if (raw === "MALE" || raw === "Male") return i18n.t("mediaKit:gender.male");
  if (raw === "FEMALE" || raw === "Female")
    return i18n.t("mediaKit:gender.female");
  if (raw === "UNDEFINED" || raw === "UNKNOWN")
    return i18n.t("mediaKit:gender.other");
  return raw;
}

function formatAgeLine(range: string): string {
  const cr = range.replace(/^age/i, "").trim();
  const n = cr === "65-" ? "65+" : cr;
  return i18n.t("mediaKit:ageYears", { range: n });
}

function genderBarKey(type: string): "male" | "female" | "unknown" {
  const raw = type.trim();
  const t = raw.toLowerCase().replace(/_/g, "");
  if (
    t === "male" ||
    t === "masculino" ||
    t === "m" ||
    raw === "MALE" ||
    raw === "Male"
  )
    return "male";
  if (
    t === "female" ||
    t === "femenino" ||
    t === "f" ||
    raw === "FEMALE" ||
    raw === "Female"
  )
    return "female";
  return "unknown";
}

const GENDER_BAR_COLORS: Record<"male" | "female" | "unknown", string> = {
  male: "#4D9EF6",
  female: "#F7A6B2",
  unknown: "#6A89CC",
};

/**
 * Audiencia: headings, label + %, and a LinearProgress bar per row (0–100).
 */
export const MediaKitDemographyTextBlock: React.FC<{
  gender?: DemographyGenderRow[];
  ageRanges?: DemographyAgeRow[];
  /** TikTok: show creator-reported disclaimer above blocks */
  showSelfReportedNote?: boolean;
}> = ({ gender, ageRanges, showSelfReportedNote }) => {
  const { t } = useTranslation("mediaKit");
  const sortedGender = [...(gender ?? [])]
    .filter((row) => {
      const t = row.type.trim().toLowerCase().replace(/_/g, "");
      return !(
        t === "genderuserspecified" ||
        t === "userspecified" ||
        (t.includes("user") && t.includes("specified"))
      );
    })
    .sort((a, b) => pct(b) - pct(a));

  const sortedAge = [...(ageRanges ?? [])].sort((a, b) => {
    const aRange = a.range.replace(/^age/i, "");
    const bRange = b.range.replace(/^age/i, "");
    const aStart = parseInt(aRange.split("-")[0]) || 0;
    const bStart = parseInt(bRange.split("-")[0]) || 0;
    return aStart - bStart;
  });

  if (sortedGender.length === 0 && sortedAge.length === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ pb: 0 }}>
      <CardHeading variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        {t("metrics.audience")}
      </CardHeading>
      {showSelfReportedNote ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("selfReported")}
        </Typography>
      ) : null}

      {sortedGender.length > 0 && (
        <Box sx={{ mb: sortedAge.length > 0 ? 3 : 0 }}>
          <CardHeading
            variant="body2"
            sx={{ mb: 2, fontWeight: 600, display: "block" }}
          >
            {t("metrics.genderDistribution")}
          </CardHeading>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {sortedGender.map((item, idx) => {
              const v = pct(item);
              const gk = genderBarKey(item.type);
              return (
                <Box
                  key={`g-${idx}-${item.type}`}
                  data-mediakit-pdf-audiencia-row=""
                  sx={{ mb: 2.5 }}
                >
                  <CardHeading
                    variant="body2"
                    data-mediakit-pdf-audiencia-label=""
                    sx={{
                      fontWeight: 600,
                      display: "block",
                      mb: 0.5,
                      textTransform: "none",
                    }}
                  >
                    {demographyGenderLabel(item.type)}
                  </CardHeading>
                  <Typography
                    variant="h6"
                    component="p"
                    data-mediakit-pdf-audiencia-pct=""
                    sx={{
                      m: 0,
                      mb: 1.25,
                      fontWeight: 600,
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    {v.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, v))}
                    sx={(theme) => ({
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: GENDER_BAR_COLORS[gk],
                        borderRadius: 4,
                      },
                    })}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {sortedAge.length > 0 && (
        <Box>
          <CardHeading
            variant="body2"
            sx={{ mb: 2, fontWeight: 600, display: "block" }}
          >
            {t("metrics.ageDistribution")}
          </CardHeading>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {sortedAge.map((item, idx) => {
              const v = pct(item);
              const cleanRange = item.range.replace(/^age/i, "").trim();
              const normalizedRange = cleanRange === "65-" ? "65+" : cleanRange;
              const barColor = getAgeRangeColor(normalizedRange);
              return (
                <Box
                  key={`a-${idx}-${item.range}`}
                  data-mediakit-pdf-audiencia-row=""
                  sx={{ mb: 2.5 }}
                >
                  <CardHeading
                    variant="body2"
                    data-mediakit-pdf-audiencia-label=""
                    sx={{
                      fontWeight: 600,
                      display: "block",
                      mb: 0.5,
                      textTransform: "none",
                    }}
                  >
                    {formatAgeLine(item.range)}
                  </CardHeading>
                  <Typography
                    variant="h6"
                    component="p"
                    data-mediakit-pdf-audiencia-pct=""
                    sx={{
                      m: 0,
                      mb: 1.25,
                      fontWeight: 600,
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    {v.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, v))}
                    sx={(theme) => ({
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: barColor,
                        borderRadius: 4,
                      },
                    })}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
